import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import EisTemplatePicker from '../../_components/EisTemplatePicker';
import ScoringFieldItem from './ScoringFieldItem';
import { useEisRuleConfigContext } from '../context';
import type { EisConfigScoringIndicator } from '../../_types';

type PointsMap = Record<string, number | null>;
type FieldPointsState = Record<number, PointsMap>;
type NumberPredicatesState = Record<number, { operator: string; value: string }>;

function buildPointsFromScores(indicator: EisConfigScoringIndicator): PointsMap {
  const map: PointsMap = {};
  switch (indicator.fieldType) {
    case 'Number': {
      map['number'] = indicator.scores[0]?.points ?? null;
      break;
    }
    case 'Boolean': {
      const trueScore = indicator.scores.find((s) => (s.predicateJson as { value: boolean }).value === true);
      const falseScore = indicator.scores.find((s) => (s.predicateJson as { value: boolean }).value === false);
      map['true'] = trueScore?.points ?? null;
      map['false'] = falseScore?.points ?? null;
      break;
    }
    case 'SingleSelect':
    case 'MultiSelect':
      (indicator.options ?? []).forEach((opt) => {
        const score = indicator.scores.find(
          (s) => (s.predicateJson as { fieldOptionId: number }).fieldOptionId === opt.fieldOptionId,
        );
        map[String(opt.fieldOptionId)] = score?.points ?? null;
      });
      break;
  }
  return map;
}

const ManageScoring: React.FC = () => {
  const { configuration, setScoringTemplates } = useEisRuleConfigContext();
  const templates = configuration?.scoring?.templates ?? [];

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [fieldPoints, setFieldPoints] = useState<FieldPointsState>({});
  const [numberPredicates, setNumberPredicates] = useState<NumberPredicatesState>({});

  const selectedTemplate = useMemo(
    () => templates.find((tmpl) => tmpl.templateId === selectedTemplateId) ?? null,
    [templates, selectedTemplateId],
  );

  const indicators = useMemo(() => selectedTemplate?.indicators ?? [], [selectedTemplate]);

  useEffect(() => {
    if (indicators.length === 0) return;
    const newPoints: FieldPointsState = {};
    const newPredicates: NumberPredicatesState = {};

    indicators.forEach((ind) => {
      newPoints[ind.fieldId] = buildPointsFromScores(ind);
      if (ind.fieldType === 'Number') {
        const score = ind.scores[0];
        const json = score ? (score.predicateJson as { from?: number; to?: number }) : {};
        let operator = 'isGreaterThanOrEqualTo';
        let value = '';
        if (json.from != null && json.to != null) {
          operator = 'isEqualTo';
          value = String(json.from);
        } else if (json.from != null) {
          operator = 'isGreaterThanOrEqualTo';
          value = String(json.from);
        } else if (json.to != null) {
          operator = 'isLessThanOrEqualTo';
          value = String(json.to);
        }
        newPredicates[ind.fieldId] = { operator, value };
      }
    });

    setFieldPoints(newPoints);
    setNumberPredicates(newPredicates);
  }, [indicators]);

  const handleTemplateChange = (templateId: number) => {
    setSelectedTemplateId(templateId);
    setSearch('');
    setFieldPoints({});
    setNumberPredicates({});
  };

  const handleScoreChange = useCallback((fieldId: number, scoreKey: string, points: number | null) => {
    setFieldPoints((prev) => ({
      ...prev,
      [fieldId]: { ...(prev[fieldId] ?? {}), [scoreKey]: points },
    }));
  }, []);

  const handleNumberPredicateChange = useCallback((fieldId: number, field: 'operator' | 'value', value: string) => {
    setNumberPredicates((prev) => ({
      ...prev,
      [fieldId]: {
        ...(prev[fieldId] ?? { operator: 'isEqualTo', value: '' }),
        [field]: value,
      },
    }));
  }, []);

  useEffect(() => {
    const updatedTemplates = templates.map((tmpl) => ({
      ...tmpl,
      indicators: tmpl.indicators.map((ind) => {
        const points = fieldPoints[ind.fieldId];
        if (!points) return ind;
        let scores = [...ind.scores];
        switch (ind.fieldType) {
          case 'Number': {
            const predicate = numberPredicates[ind.fieldId];
            const numVal = predicate?.value !== '' && predicate?.value != null ? Number(predicate.value) : undefined;
            let predicateJson: Record<string, unknown> = {};
            if (numVal != null) {
              switch (predicate?.operator) {
                case 'isEqualTo':
                  predicateJson = { from: numVal, to: numVal };
                  break;
                case 'isGreaterThan':
                case 'isGreaterThanOrEqualTo':
                  predicateJson = { from: numVal };
                  break;
                case 'isLessThan':
                case 'isLessThanOrEqualTo':
                  predicateJson = { to: numVal };
                  break;
                default:
                  predicateJson = { from: numVal };
              }
            }
            scores = [
              {
                ...( ind.scores[0] ?? { id: 0, predicateType: 'NumberRange' as const, predicateJson: {}, label: undefined }),
                predicateType: 'NumberRange' as const,
                predicateJson,
                points: points['number'] ?? null,
              },
            ];
            break;
          }
          case 'Boolean': {
            scores = [
              {
                ...( ind.scores.find((s) => (s.predicateJson as { value: boolean }).value === true) ?? { id: 0, predicateType: 'Boolean' as const, predicateJson: { value: true }, label: undefined }),
                predicateType: 'Boolean' as const,
                predicateJson: { value: true },
                points: points['true'] ?? null,
              },
              {
                ...( ind.scores.find((s) => (s.predicateJson as { value: boolean }).value === false) ?? { id: 0, predicateType: 'Boolean' as const, predicateJson: { value: false }, label: undefined }),
                predicateType: 'Boolean' as const,
                predicateJson: { value: false },
                points: points['false'] ?? null,
              },
            ];
            break;
          }
          case 'SingleSelect':
          case 'MultiSelect': {
            scores = (ind.options ?? []).map((opt) => {
              const existing = ind.scores.find(
                (s) => (s.predicateJson as { fieldOptionId: number }).fieldOptionId === opt.fieldOptionId,
              );
              return {
                ...(existing ?? { id: 0, predicateType: 'OptionMatch' as const, predicateJson: { fieldOptionId: opt.fieldOptionId }, label: undefined }),
                predicateType: 'OptionMatch' as const,
                predicateJson: { fieldOptionId: opt.fieldOptionId },
                points: points[String(opt.fieldOptionId)] ?? null,
              };
            });
            break;
          }
        }
        return { ...ind, scores };
      }),
    }));
    setScoringTemplates(updatedTemplates);
  }, [fieldPoints, numberPredicates, templates, setScoringTemplates]);

  const filteredIndicators = useMemo(() => {
    if (!search.trim()) return indicators;
    const lower = search.trim().toLowerCase();
    return indicators.filter((ind) => (ind.fieldName ?? String(ind.fieldId)).toLowerCase().includes(lower));
  }, [indicators, search]);

  const pickerOptions = useMemo(
    () => templates.map((tmpl) => ({ id: tmpl.templateId, name: tmpl.templateName })),
    [templates],
  );

  const hasTemplate = selectedTemplateId != null;

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: neutral[50],
          border: '1px solid',
          borderColor: neutral[100],
          borderRadius: 3,
          p: 6,
        }}
      >
        <EisTemplatePicker
          id='eis-config-template-picker'
          value={selectedTemplateId ?? null}
          onChange={(id) => {
            if (id != null) handleTemplateChange(id);
          }}
          options={pickerOptions}
          placeholder={t('performanceReporting.eis.configuration.manageScoring.templatePlaceholder')}
          size='small'
          sx={{ width: 260, paddingTop: 0 }}
        />

        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('performanceReporting.eis.configuration.manageScoring.searchPlaceholder')}
          size='small'
          disabled={!hasTemplate}
          sx={{ width: 280, paddingTop: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon
                  fontSize='small'
                  sx={{ color: neutral[400] }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {!hasTemplate && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography
            variant='bodyMd'
            color='text.secondary'
          >
            {t('performanceReporting.eis.configuration.manageScoring.selectTemplatePrompt')}
          </Typography>
        </Box>
      )}

      {hasTemplate && filteredIndicators.length === 0 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography
            variant='bodyMd'
            color='text.secondary'
          >
            {t('performanceReporting.eis.configuration.manageScoring.noFieldsFound')}
          </Typography>
        </Box>
      )}

      {hasTemplate && filteredIndicators.length > 0 && (
        <Stack spacing={1.5}>
          {filteredIndicators.map((indicator) => (
            <ScoringFieldItem
              key={indicator.fieldId}
              indicator={indicator}
              points={fieldPoints[indicator.fieldId] ?? {}}
              onScoreChange={handleScoreChange}
              onNumberPredicateChange={handleNumberPredicateChange}
              numberPredicate={numberPredicates[indicator.fieldId] ?? { operator: 'isGreaterThanOrEqualTo', value: '' }}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default ManageScoring;
